import React, { useEffect, useRef } from 'react';

const AIWidgets = ({ locale = 'en' }) => {
    const scriptLoadedRef = useRef(false);
    const currentRootIdRef = useRef(null);
    const initTimeoutRef = useRef(null);

    useEffect(() => {
        // Clean up previous chatbot instance - more thorough cleanup
        const cleanupPreviousInstance = () => {
            // Remove all existing Jotform Agent instances by ID
            const rootIds = [
                'JotformAgent-019673655c95793380fccc9651b214cf73d8',
                'JotformAgent-019673529df77fc2a7572a8ace76859dd4a9'
            ];

            rootIds.forEach(rootId => {
                const element = document.getElementById(rootId);
                if (element) {
                    // Remove all child elements first
                    while (element.firstChild) {
                        element.firstChild.remove();
                    }
                    element.remove();
                }
            });

            // Remove all containers that match the pattern
            const chatbotContainers = document.querySelectorAll('[id^="JotformAgent-"]');
            chatbotContainers.forEach(container => {
                // Remove all child elements first
                while (container.firstChild) {
                    container.firstChild.remove();
                }
                container.remove();
            });

            // Remove any iframe elements related to the chatbot
            const chatbotIframes = document.querySelectorAll('iframe[src*="jotform.com"]');
            chatbotIframes.forEach(iframe => {
                if (iframe.src.includes('agent.jotform.com')) {
                    iframe.remove();
                }
            });

            // Remove any chatbot-related divs that might be floating
            const allDivs = document.querySelectorAll('div');
            allDivs.forEach(div => {
                if (div.id && div.id.startsWith('JotformAgent-')) {
                    // Remove all child elements first
                    while (div.firstChild) {
                        div.firstChild.remove();
                    }
                    div.remove();
                }
            });

            // Clear any existing root ID reference
            currentRootIdRef.current = null;
        };

        // Load Jotform Agent Script only once
        let jotformScript;
        if (!scriptLoadedRef.current) {
            jotformScript = document.createElement('script');
            jotformScript.src = 'https://cdn.jotfor.ms/s/umd/latest/for-embedded-agent.js';
            jotformScript.async = true;
            jotformScript.id = 'jotform-agent-script';
            document.body.appendChild(jotformScript);
            scriptLoadedRef.current = true;
        } else {
            // Script already loaded, use existing one
            jotformScript = document.getElementById('jotform-agent-script');
        }

        const initializeChatbot = () => {
            // Clear any pending initialization
            if (initTimeoutRef.current) {
                clearTimeout(initTimeoutRef.current);
            }

            // Clean up previous instance first
            cleanupPreviousInstance();

            // Use requestAnimationFrame to ensure DOM cleanup is complete, then setTimeout for re-initialization
            // This ensures the browser has processed all DOM removals before creating a new instance
            requestAnimationFrame(() => {
                initTimeoutRef.current = setTimeout(() => {
                    if (window.AgentInitializer) {
                        const isFrench = locale === 'fr';
                        const rootId = isFrench
                            ? 'JotformAgent-019673655c95793380fccc9651b214cf73d8'
                            : 'JotformAgent-019673529df77fc2a7572a8ace76859dd4a9';

                        // Final check - remove any remaining instances
                        const existingInstance = document.getElementById(rootId);
                        if (existingInstance) {
                            // Force remove with all children
                            while (existingInstance.firstChild) {
                                existingInstance.firstChild.remove();
                            }
                            existingInstance.remove();
                        }

                        // Additional cleanup - search for any remaining chatbot elements
                        const remainingContainers = document.querySelectorAll('[id^="JotformAgent-"]');
                        remainingContainers.forEach(container => {
                            while (container.firstChild) {
                                container.firstChild.remove();
                            }
                            container.remove();
                        });

                        currentRootIdRef.current = rootId;

                        window.AgentInitializer.init({
                            agentRenderURL: isFrench
                                ? 'https://agent.jotform.com/019673655c95793380fccc9651b214cf73d8'
                                : 'https://agent.jotform.com/019673529df77fc2a7572a8ace76859dd4a9',
                            rootId: rootId,
                            formID: isFrench
                                ? '019673655c95793380fccc9651b214cf73d8'
                                : '019673529df77fc2a7572a8ace76859dd4a9',
                            queryParams: ['skipWelcome=1', 'maximizable=1'],
                            domain: 'https://www.jotform.com',
                            isDraggable: false,
                            background: 'linear-gradient(180deg, #6C73A8 0%, #6C73A8 100%)',
                            buttonBackgroundColor: '#0066C3',
                            buttonIconColor: '#FFFFFF',
                            variant: false,
                            customizations: {
                                greeting: 'Yes',
                                greetingMessage: isFrench
                                    ? 'Bonjour ! Comment puis-je vous aider ?'
                                    : 'Hi! How can I help?',
                                openByDefault: 'No',
                                pulse: 'No',
                                position: 'right',
                                autoOpenChatIn: '0',
                            },
                            isVoice: false,
                        });
                    }
                }, 150); // Small delay to ensure cleanup completes
            });
        };

        if (scriptLoadedRef.current && window.AgentInitializer) {
            // Script already loaded, initialize with cleanup
            initializeChatbot();
        } else if (jotformScript) {
            // Wait for script to load
            jotformScript.onload = initializeChatbot;
        }

        return () => {
            // Clear any pending initialization
            if (initTimeoutRef.current) {
                clearTimeout(initTimeoutRef.current);
            }
            // Cleanup on unmount or locale change
            cleanupPreviousInstance();
        };
    }, [locale]);

    return <></>;
};

export default AIWidgets;